import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  Location,
  ShopifyOrderSkipReason,
  ShopifyOrderView,
  ShopifyTransferSuggestion,
  WebOrderAttentionReason,
} from "../api/types";
import { useAuth } from "../state/AuthContext";
import { useBoxPickPrompt } from "../components/shop-floor/useBoxPickPrompt";
import { WebOrdersByShop } from "../components/shopify/report/WebOrdersByShop";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Chip } from "../components/ui/Chip";
import { SkipWebOrderModal } from "../components/shopify/SkipWebOrderModal";
import { PassWebOrderModal } from "../components/shopify/PassWebOrderModal";
import { WebOrdersToAssign } from "../components/shopify/WebOrdersToAssign";
import { WebOrderAttentionBox } from "../components/shopify/WebOrderAttentionBox";
import { WebOrderMoney } from "../components/orders/WebOrderMoney";
import { webPaymentState } from "../components/orders/orderMoneyUi";
import {
  REASON_LABEL,
  REASON_ORDER,
  SKIP_REASON_LABEL,
  byUrgency,
  leadReason,
  toneClass,
} from "../components/shopify/webOrderAttention";
import { whenText } from "../components/shopify/report/webOrdersText";

/** G-167 (OD-283): "To assign" — the office's list of orders due later than tomorrow. */
type OrdersTab = "to-assign" | "to-handle" | "by-shop";

/** G-160: the To handle filter — every order, those needing someone, one reason, or the skipped. */
type ListFilter = "all" | "needs" | "skipped" | WebOrderAttentionReason;

const SKIPPABLE = new Set(["NEW", "OFFERED", "UNFULFILLABLE"]);

const ACTIVE_STATUSES = new Set(["OFFERED", "PREPARING", "UNFULFILLABLE"]);

function qtyText(fixed: string): string {
  return String(Number(fixed));
}

/** Opens the inventory page on the source shop with the transfer form prefilled. */
function transferLink(transfer: ShopifyTransferSuggestion): string {
  const params = new URLSearchParams({
    locationId: transfer.fromLocationId,
    transferTo: transfer.toLocationId,
  });
  // A roll's length is the person's call; only a real quantity is prefilled.
  if (!transfer.packaging) params.set("transferQty", qtyText(transfer.qty));
  if (transfer.itemId) params.set("transferItem", transfer.itemId);
  return `/app/inventory?${params.toString()}`;
}

function transferText(transfer: ShopifyTransferSuggestion): string {
  return transfer.packaging
    ? `A roll of ${transfer.description} from ${transfer.fromLocationName}`
    : `${qtyText(transfer.qty)} ${transfer.description} from ${transfer.fromLocationName}`;
}


function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function eur(value: string): string {
  return `€${Number(value).toFixed(2)}`;
}

function lineSummary(order: ShopifyOrderView): string {
  return order.lines.map((line) => `${Number(line.quantity)} x ${line.titleRaw}`).join(", ");
}

function statusLabel(status: string): string {
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (letter) => letter.toUpperCase());
}

export function ShopifyOrdersPage() {
  const { hasRole, user } = useAuth();
  // G-112 review fix (OD-47): fulfilling sells the order's shop items — an
  // item in a storage box asks "Is this from box X?" (as SaleNewPage does).
  const boxPrompt = useBoxPickPrompt();
  const [orders, setOrders] = useState<ShopifyOrderView[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // G-143: "By shop" is the web-orders report; `?tab=by-shop` opens it, with
  // `shop`, `from` and `to` when a person page links back.
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwner = hasRole("OWNER");
  // G-167 (OD-283): assigning and passing are the owner's and an admin's.
  const isOffice = hasRole("OWNER", "ADMIN");
  const tabParam = searchParams.get("tab");
  const tab: OrdersTab =
    tabParam === "by-shop" ? "by-shop" : tabParam === "to-assign" && isOffice ? "to-assign" : "to-handle";
  const chooseTab = (next: OrdersTab) => {
    setSearchParams(next === "to-handle" ? {} : { tab: next }, { replace: true });
  };
  // G-160: the bell opens `?order=<id>` (every order shown); otherwise the
  // list starts on the orders that need someone.
  const linkedOrderId = searchParams.get("order");
  const [filter, setFilter] = useState<ListFilter>(linkedOrderId ? "all" : "needs");
  const [skippedOrders, setSkippedOrders] = useState<ShopifyOrderView[]>([]);
  const [skipping, setSkipping] = useState<ShopifyOrderView | null>(null);
  const [skipError, setSkipError] = useState<string | null>(null);
  const [passing, setPassing] = useState<ShopifyOrderView | null>(null);
  const [passError, setPassError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  // A bell or "Fix it" link: show every order, with that one open — even
  // when the page was already open on another filter.
  useEffect(() => {
    if (!linkedOrderId) return;
    setFilter("all");
    setSelectedOrderId(linkedOrderId);
  }, [linkedOrderId]);
  // Picking a filter starts its list at the top: the card never shows an
  // order the chosen list does not hold.
  const pickFilter = (next: ListFilter) => {
    setFilter(next);
    setSelectedOrderId(null);
  };

  // The orders are asked for again every 10 s, so a new web order shows up
  // without a reload (it only refreshes this list and the tab's count). The
  // shop list for the owner's picker does not change minute to minute: once.
  const load = useCallback(async () => {
    setError(null);
    try {
      const [open, skipped] = await Promise.all([
        api.listShopifyOrders(),
        isOwner ? api.listShopifyOrders({ view: "skipped" }) : Promise.resolve([]),
      ]);
      setOrders(open);
      setSkippedOrders(skipped);
    } catch (e) {
      setError(errText(e, "Could not load Shopify orders"));
    }
  }, [isOwner]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 10000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!hasRole("OWNER", "ADMIN")) return;
    api
      .listLocations()
      .then((rows) => setLocations(rows.filter((location) => location.isActive)))
      .catch((e: unknown) => setError(errText(e, "Could not load the shops")));
  }, [hasRole]);

  // G-160: a handed-over order whose courier could not deliver it is
  // "to handle" again; so is every order that needs someone — and (G-167)
  // one waiting in "To assign", so it can still be opened and skipped.
  const activeOrders = useMemo(
    () =>
      orders
        .filter((order) => ACTIVE_STATUSES.has(order.status) || order.attention.length > 0 || order.toAssign)
        .sort(byUrgency),
    [orders],
  );
  const reasonCounts = useMemo(() => {
    const counts = new Map<WebOrderAttentionReason, number>();
    for (const order of activeOrders) {
      for (const reason of order.attention) counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return counts;
  }, [activeOrders]);
  const needsSomeone = activeOrders.filter((order) => order.attention.length > 0);
  const toAssignCount = orders.filter((order) => order.toAssign).length;
  const shownOrders =
    filter === "skipped"
      ? skippedOrders
      : filter === "all"
        ? activeOrders
        : filter === "needs"
          ? needsSomeone
          : activeOrders.filter((order) => order.attention.includes(filter));
  // The chosen order wherever it is (a bell may name one handed over since);
  // otherwise the first of the list on screen.
  const selectedOrder =
    [...orders, ...skippedOrders].find((order) => order.id === selectedOrderId) ??
    shownOrders[0] ??
    null;

  const skip = async (order: ShopifyOrderView, reason: ShopifyOrderSkipReason, note: string | undefined) => {
    setBusy(true);
    setSkipError(null);
    try {
      await api.skipShopifyOrder(order.id, { reason, note });
      setSkipping(null);
      await load();
    } catch (e) {
      setSkipError(errText(e, "Could not skip the order"));
    } finally {
      setBusy(false);
    }
  };

  const unskip = async (order: ShopifyOrderView) => {
    setBusy(true);
    setError(null);
    try {
      await api.unskipShopifyOrder(order.id);
      await load();
    } catch (e) {
      setError(errText(e, "Could not undo the skip"));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (order: ShopifyOrderView, locationId: string) => {
    setBusy(true);
    setAssignError(null);
    try {
      await api.assignShopifyOrder(order.id, locationId);
      await load();
    } catch (e) {
      setAssignError(errText(e, "Could not assign the order"));
    } finally {
      setBusy(false);
    }
  };

  const pass = async (order: ShopifyOrderView, locationId: string, reason: string) => {
    setBusy(true);
    setPassError(null);
    try {
      await api.passShopifyOrder(order.id, locationId, reason);
      setPassing(null);
      await load();
    } catch (e) {
      setPassError(errText(e, "Could not pass the order"));
    } finally {
      setBusy(false);
    }
  };

  const startPass = (order: ShopifyOrderView) => {
    setPassError(null);
    setPassing(order);
  };

  const accept = async (order: ShopifyOrderView) => {
    setBusy(true);
    setError(null);
    try {
      const locationId = hasRole("OWNER")
        ? selectedLocationId || order.offeredToLocationId || undefined
        : undefined;
      await api.acceptShopifyOrder(order.id, locationId);
      await load();
    } catch (e) {
      setError(errText(e, "Could not accept order"));
    } finally {
      setBusy(false);
    }
  };

  const fulfil = async (order: ShopifyOrderView) => {
    setBusy(true);
    setError(null);
    try {
      await boxPrompt.run((boxAnswers) => api.fulfilShopifyOrder(order.id, boxAnswers));
      await load();
    } catch (e) {
      setError(errText(e, "Could not hand order to delivery"));
    } finally {
      setBusy(false);
    }
  };

  const recheck = async (order: ShopifyOrderView) => {
    setBusy(true);
    setError(null);
    try {
      await api.reEvaluateShopifyOrder(order.id);
      await load();
    } catch (e) {
      setError(errText(e, "Could not re-check the order"));
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async (order: ShopifyOrderView) => {
    setBusy(true);
    setError(null);
    try {
      await api.acknowledgeShopifyChange(order.id);
      await load();
    } catch (e) {
      setError(errText(e, "Could not acknowledge the change"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {boxPrompt.element}
      <div className="page-header">
        <div>
          <h2 className="page-title">Web orders</h2>
          {tab === "to-handle" ? (
            <p className="page-subtitle">
              {activeOrders.length} active order{activeOrders.length === 1 ? "" : "s"} need attention.
            </p>
          ) : null}
        </div>
        <button type="button" className="button" onClick={() => void api.syncShopifyNow().then(load)} disabled={busy}>
          Sync now
        </button>
      </div>

      <SegmentedControl
        ariaLabel="Web orders views"
        options={[
          ...(isOffice
            ? [{ value: "to-assign" as const, label: toAssignCount ? `To assign · ${toAssignCount}` : "To assign" }]
            : []),
          { value: "to-handle", label: activeOrders.length ? `To handle · ${activeOrders.length}` : "To handle" },
          { value: "by-shop", label: "By shop" },
        ]}
        value={tab}
        onChange={chooseTab}
      />

      {tab === "to-handle" ? (
        <div className="wo-filter" role="group" aria-label="Show">
          <Chip label="Needs someone" count={needsSomeone.length} selected={filter === "needs"} onClick={() => pickFilter("needs")} />
          <Chip label="All" count={activeOrders.length} selected={filter === "all"} onClick={() => pickFilter("all")} />
          {REASON_ORDER.filter((reason) => reasonCounts.has(reason)).map((reason) => (
            <Chip
              key={reason}
              label={REASON_LABEL[reason]}
              count={reasonCounts.get(reason)}
              selected={filter === reason}
              onClick={() => pickFilter(reason)}
            />
          ))}
          {isOwner ? (
            <Chip label="Skipped" count={skippedOrders.length} selected={filter === "skipped"} onClick={() => pickFilter("skipped")} />
          ) : null}
        </div>
      ) : null}

      {tab === "by-shop" ? <WebOrdersByShop initialParams={searchParams} /> : null}

      {tab === "to-assign" ? (
        <WebOrdersToAssign
          orders={orders}
          locations={locations}
          busy={busy}
          error={assignError ?? error}
          onAssign={(order, locationId) => void assign(order, locationId)}
          onPass={startPass}
        />
      ) : null}

      {tab === "to-handle" && error ? <div className="alert error">{error}</div> : null}

      {tab === "to-handle" ? (
        <div className="shopify-orders-layout">
          <div className="card table-scroll">
            <table>
              <thead>
                <tr><th>Order</th><th>Status</th><th>Total</th></tr>
              </thead>
              <tbody>
                {shownOrders.length === 0 ? (
                  <tr><td colSpan={3} className="muted">{filter === "skipped" ? "No skipped web orders." : "No web orders here."}</td></tr>
                ) : null}
                {shownOrders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => setSelectedOrderId(order.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <strong>{order.orderNumber ?? order.shopifyOrderId}</strong>
                      <br />
                      <span className="muted">{order.customerName ?? "No customer name"}</span>
                    </td>
                    <td>
                      <StatusTag order={order} />
                    </td>
                    <td>
                      {eur(order.totalEur)}
                      <br />
                      <PaymentPill order={order} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedOrder ? (
            <article className="card" style={{ padding: "var(--space-4)", display: "grid", gap: "var(--space-3)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)", alignItems: "start" }}>
                <div>
                  <h3 style={{ margin: 0 }}>{selectedOrder.orderNumber ?? selectedOrder.shopifyOrderId}</h3>
                  <p className="muted" style={{ margin: 0 }}>{lineSummary(selectedOrder)}</p>
                </div>
                <span className="badge primary">{statusLabel(selectedOrder.status)}</span>
              </div>

              <WebOrderAttentionBox order={selectedOrder} />
              <WebOrderMoney shopify={selectedOrder.money} />
              {selectedOrder.fulfillmentMethod === "PICK_UP" ? (
                <p className="muted" style={{ margin: 0 }}>Shopify · customer pickup{selectedOrder.offeredToLocation ? ` at ${selectedOrder.offeredToLocation.name}` : ""}</p>
              ) : null}
              {selectedOrder.pickupRoutingIssue ? (
                <div className="alert error">
                  <strong>Pickup shop needs attention</strong>
                  <p>{selectedOrder.pickupRoutingIssue}</p>
                  {isOwner ? <Link to="/app/locations/manage">Map Shopify pickup locations</Link> : null}
                </div>
              ) : null}

              {selectedOrder.skipped ? (
                <div className="alert">
                  <strong>Skipped · {SKIP_REASON_LABEL[selectedOrder.skipped.reason]}</strong>
                  {selectedOrder.skipped.note ? <p>{selectedOrder.skipped.note}</p> : null}
                  <p className="muted">
                    {selectedOrder.skipped.test ? "Shopify" : (selectedOrder.skipped.by?.fullName ?? "An owner")} · {whenText(selectedOrder.skipped.at)}
                  </p>
                  {selectedOrder.skipped.test ? null : (
                    <button type="button" className="button plain" disabled={busy} onClick={() => void unskip(selectedOrder)}>
                      Undo skip
                    </button>
                  )}
                </div>
              ) : null}

              {selectedOrder.shopifyChange ? (
                <div className="alert error">
                  <strong>
                    Changed in Shopify after{" "}
                    {selectedOrder.status === "FULFILLED" ? "hand-over" : "acceptance"}
                  </strong>
                  <ul>
                    {selectedOrder.shopifyChange.changes.map((change) => (
                      <li key={change}>{change}</li>
                    ))}
                  </ul>
                  <p className="muted">The shop keeps the lines it accepted.</p>
                  <button type="button" className="button plain" disabled={busy} onClick={() => void acknowledge(selectedOrder)}>
                    Acknowledge
                  </button>
                </div>
              ) : null}

              <div className="card" style={{ padding: "var(--space-3)" }}>
                <strong>Lines</strong>
                <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
                  {selectedOrder.lines.map((line) => (
                    <div key={line.id} style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-2)" }}>
                      <span>{Number(line.quantity)} x {line.titleRaw}</span>
                      {line.kind === "UNLINKED" && line.shopifyVariantId ? (
                        <Link to={`/app/shopify/products?variant=${encodeURIComponent(line.shopifyVariantId)}`}>
                          Needs link — link it
                        </Link>
                      ) : (
                        <span>{line.kind === "BOUQUET" ? (line.bouquetBuildId ? "Built" : "Needs build") : line.kind === "UNLINKED" ? "Needs link" : "Plain item"}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {selectedOrder.status === "UNFULFILLABLE" && !selectedOrder.skipped ? (
                <div className="alert error">
                  <strong>{selectedOrder.pickupRoutingIssue ? "Pickup cannot be routed yet" : "No shop can make this order"}</strong>
                  <ul>
                    {(selectedOrder.feasibility?.shortfall ?? []).map((line) => (
                      <li key={line.description}>{line.text}</li>
                    ))}
                  </ul>
                  <p className="muted">
                    Receive or move the missing stock, or link the product. Orders are re-checked every
                    two minutes; Re-check does it now.
                  </p>
                  <button type="button" className="button plain" disabled={busy} onClick={() => void recheck(selectedOrder)}>
                    {busy ? "Checking..." : "Re-check"}
                  </button>
                </div>
              ) : null}

              {selectedOrder.status === "OFFERED" && selectedOrder.offeredToLocation && !selectedOrder.skipped ? (
                <div className="alert">
                  <strong>Offered to {selectedOrder.offeredToLocation.name}</strong>
                  <p className="muted">
                    {selectedOrder.fulfillmentMethod === "PICK_UP"
                      ? `The customer chose pickup here. Bring missing stock to ${selectedOrder.offeredToLocation.name}; do not change the pickup shop automatically.`
                      : `No single shop holds everything; ${selectedOrder.offeredToLocation.name} holds most of it and can accept now. Bring in the rest, then Re-check.`}
                  </p>
                  <ul>
                    {(selectedOrder.transferPlan ?? []).map((transfer, index) => (
                      <li key={`${index}-${transfer.itemId ?? transfer.description}`}>
                        {transferText(transfer)}
                        {" · "}
                        <Link to={transferLink(transfer)}>Transfer now</Link>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="button plain" disabled={busy} onClick={() => void recheck(selectedOrder)}>
                    {busy ? "Checking..." : "Re-check"}
                  </button>
                </div>
              ) : null}

              {selectedOrder.status === "PREPARING" && selectedOrder.transferPlan?.length ? (
                <div className="alert">
                  <strong>Still to bring in</strong>
                  <ul>
                    {selectedOrder.transferPlan.map((transfer, index) => (
                      <li key={`${index}-${transfer.itemId ?? transfer.description}`}>
                        {transferText(transfer)}
                        {" · "}
                        <Link to={transferLink(transfer)}>Transfer now</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {selectedOrder.status === "OFFERED" && !selectedOrder.skipped ? (
                <div style={{ display: "grid", gap: "var(--space-2)" }}>
                  {hasRole("OWNER") ? (
                    <select value={selectedLocationId} onChange={(e) => setSelectedLocationId(e.target.value)}>
                      <option value="">
                        {selectedOrder.offeredToLocation
                          ? `Accept as ${selectedOrder.offeredToLocation.name}`
                          : "Use the only feasible shop, or choose one"}
                      </option>
                      {locations.filter((location) => selectedOrder.fulfillmentMethod !== "PICK_UP" || location.id === selectedOrder.offeredToLocationId).map((location) => (
                        <option key={location.id} value={location.id}>{location.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="muted" style={{ margin: 0 }}>Accepting as {user?.assignedLocation?.name ?? "your assigned shop"}.</p>
                  )}
                  <button type="button" className="button" disabled={busy} onClick={() => void accept(selectedOrder)}>
                    {busy ? "Accepting..." : "Accept order"}
                  </button>
                </div>
              ) : null}

              {selectedOrder.status === "PREPARING" ? (
                <div style={{ display: "grid", gap: "var(--space-2)" }}>
                  <p className="muted" style={{ margin: 0 }}>
                    Prepare bouquet lines in the shop-floor builder. Once all bouquet lines are built, {selectedOrder.fulfillmentMethod === "PICK_UP" ? "record customer collection" : "hand the order to delivery"} here.
                  </p>
                  <button
                    type="button"
                    className="button"
                    disabled={
                      busy ||
                      selectedOrder.lines.some((line) => line.kind === "BOUQUET" && !line.bouquetBuildId)
                    }
                    onClick={() => void fulfil(selectedOrder)}
                  >
                    {busy ? "Completing..." : selectedOrder.fulfillmentMethod === "PICK_UP" ? "Customer collected" : "Hand to delivery"}
                  </button>
                  {isOffice && selectedOrder.fulfillmentMethod !== "PICK_UP" ? (
                    <button type="button" className="button plain" disabled={busy} onClick={() => startPass(selectedOrder)}>
                      Pass to another shop
                    </button>
                  ) : null}
                </div>
              ) : null}
              {isOwner && !selectedOrder.skipped && !selectedOrder.claimedLocationId && SKIPPABLE.has(selectedOrder.status) ? (
                <button type="button" className="button plain wo-skip-button" disabled={busy} onClick={() => { setSkipError(null); setSkipping(selectedOrder); }}>
                  Skip
                </button>
              ) : null}
            </article>
          ) : null}
        </div>
      ) : null}

      {passing ? (
        <PassWebOrderModal
          order={passing}
          busy={busy}
          error={passError}
          onConfirm={(locationId, reason) => void pass(passing, locationId, reason)}
          onClose={() => setPassing(null)}
        />
      ) : null}

      {skipping ? (
        <SkipWebOrderModal
          order={skipping}
          busy={busy}
          error={skipError}
          onConfirm={(reason, note) => void skip(skipping, reason, note)}
          onClose={() => setSkipping(null)}
        />
      ) : null}
    </section>
  );
}

/** KI-150: Shopify's payment state under the row's total. */
function PaymentPill({ order }: { order: ShopifyOrderView }) {
  const state = webPaymentState(order.money.financialStatus);
  return <span className={state.className}>{state.label}</span>;
}

/** A row's status: its most urgent reason, else Skipped, else the plain status. */
function StatusTag({ order }: { order: ShopifyOrderView }) {
  const reason = leadReason(order);
  if (reason) return <span className={toneClass(reason)}>{REASON_LABEL[reason]}</span>;
  if (order.skipped?.test) return <span className="badge muted">Test order</span>;
  if (order.skipped) return <span className="badge muted">Skipped</span>;
  if (order.toAssign) return <span className="badge muted">To assign</span>;
  return <span className="badge primary">{statusLabel(order.status)}</span>;
}
